import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';

interface ChooseBaseModalProps {
  open: boolean;
  onClose: () => void;
  currentBaseId: string | null;
}

export const ChooseBaseModal: React.FC<ChooseBaseModalProps> = React.memo(({
  open,
  onClose,
  currentBaseId,
}) => {
  const updateBaseType = useDirtyTrackingStore((s) => s.updateBaseType);
  const rawBaseTypes = useReferenceDataStore((s) => s.baseTypes);
  const allBaseTypes = React.useMemo(
    () => [...rawBaseTypes].sort((a, b) => a.name.localeCompare(b.name)),
    [rawBaseTypes]
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setSelectedId(currentBaseId);
    setError(null);
  }, [open, currentBaseId]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSave = () => {
    updateBaseType(selectedId);
    handleClose();
  };

  const hasChanged = selectedId !== currentBaseId;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Choose Base</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {allBaseTypes.length > 0 && (
          <List
            dense
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              maxHeight: 400,
              overflowY: 'auto',
              py: 0,
            }}
          >
            <ListItemButton
              selected={selectedId === null}
              onClick={() => setSelectedId(null)}
            >
              <ListItemText
                primary={<em>None</em>}
                secondary="No base selected"
              />
            </ListItemButton>
            {allBaseTypes.map((bt) => (
              <ListItemButton
                key={bt.id}
                selected={selectedId === bt.id}
                onClick={() => setSelectedId(bt.id)}
              >
                <ListItemText
                  primary={bt.name}
                  secondary={bt.description}
                />
              </ListItemButton>
            ))}
          </List>
        )}

        {allBaseTypes.length === 0 && !error && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No bases available.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!hasChanged}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
});
